/* eslint-disable */
import React, { useState, useEffect } from "react";
import { Redirect } from "react-router-dom";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Box,
  Button,
  Divider,
  Paper,
  TextField,
  MenuItem,
  Chip,
  CircularProgress
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import ListAltIcon from "@material-ui/icons/ListAlt";
import AddBoxIcon from "@material-ui/icons/AddBox";
import AssessmentIcon from "@material-ui/icons/Assessment";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import moment from "moment";
import "moment/locale/th";

import { FinanceBudgetService } from "_services/financebudget.service";
import ProtectMoneyList from "./ProtectMoneyList";
import ProtectMoneyForm from "./ProtectMoneyForm";
import ProtectPaymentManager from "./ProtectPaymentManager";
import PrintProtectDialog from "./PrintProtectDialog";

moment.locale("th");

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    fontFamily: '"Sarabun", "Tahoma", sans-serif'
  },
  titleHeader: {
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  mainTitle: {
    fontWeight: "bold",
    color: "#1e3a8a",
    fontSize: "24px",
    display: "flex",
    alignItems: "center"
  },
  kpiCard: {
    borderRadius: 8,
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
    borderLeft: "5px solid #3b82f6",
    backgroundColor: "#ffffff",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 16px rgba(0,0,0,0.1)"
    }
  },
  kpiHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1)
  },
  kpiLabel: {
    fontSize: "14px",
    color: "#475569",
    fontWeight: "600"
  },
  kpiValue: {
    fontSize: "22px",
    fontWeight: "bold",
    marginTop: 4
  },
  kpiSubtext: {
    fontSize: "12px",
    color: "#64748b",
    marginTop: 6
  },
  tabNavigation: {
    marginBottom: theme.spacing(3),
    backgroundColor: "#ffffff",
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
  }
}));

// ฟังก์ชันคำนวณปีงบประมาณไทยปัจจุบัน (1 ต.ค. เป็นต้นไปนับเป็นปีงบประมาณถัดไป)
const getCurrentFiscalYear = () => {
  const now = moment();
  return (now.month() >= 9 ? now.year() + 543 + 1 : now.year() + 543).toString();
};

export default function FinanceBudgetView() {
  const classes = useStyles();

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("currentUser");
      return u ? JSON.parse(u) : null;
    } catch {
      return null;
    }
  })();

  const isFinanceAccount = (() => {
    if (!currentUser) return false;
    const userDepts = Array.isArray(currentUser.userDepartment)
      ? currentUser.userDepartment
      : Array.isArray(currentUser.userView?.userDepartment)
      ? currentUser.userView.userDepartment
      : [];

    const hasBudgetDep = userDepts.some(u => {
      const depKey = Number(u.departmentKey);
      return depKey === 102 || depKey === 135 || depKey === 157;
    });

    const isAdmin =
      currentUser?.roles?.includes("Admin") ||
      currentUser?.userName === "madarakung" ||
      userDepts.some(u => Number(u.departmentKey) === 108);

    return hasBudgetDep || isAdmin;
  })();

  if (!isFinanceAccount) {
    return <Redirect to="/finance/slip" />;
  }

  const [activeTab, setActiveTab] = useState(0);
  const [currentYear, setCurrentYear] = useState(() => getCurrentFiscalYear());
  const [years, setYears] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Dashboard summary KPIs
  const [summary, setSummary] = useState({
    totalProjectBudget: 0,
    totalProtected: 0,
    totalPaid: 0,
    totalRemainingBudget: 0,
    totalPendingPayment: 0,
    totalProtectCount: 0,
    completedProtectCount: 0,
    pendingProtectCount: 0
  });

  // State for Payment Manager Modal
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedProtectId, setSelectedProtectId] = useState(null);
  const [selectedProtectNumber, setSelectedProtectNumber] = useState(null);

  // State for Print Modal
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printData, setPrintData] = useState(null);

  // State for Edit Form
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    initDashboard();
  }, []);

  const initDashboard = async () => {
    const fiscalYear = getCurrentFiscalYear();
    let selected = fiscalYear;
    try {
      const yRes = await FinanceBudgetService.getYears();
      if (yRes.data?.success && yRes.data?.data?.length > 0) {
        setYears(yRes.data.data);
        const exists = yRes.data.data.some(y => y.value === fiscalYear);
        selected = exists ? fiscalYear : yRes.data.data[0].value;
        setCurrentYear(selected);
      }
    } catch (e) {
      console.error("Fetch years error:", e);
    }
    await loadSummary(selected);
  };

  const loadSummary = async yearToQuery => {
    const targetYear = yearToQuery || currentYear || getCurrentFiscalYear();
    setLoadingSummary(true);
    try {
      const res = await FinanceBudgetService.getDashboardSummary(targetYear);
      if (res.data?.success) {
        setSummary(res.data.data);
      }
    } catch (err) {
      console.error("Load summary error:", err);
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleYearChange = newYear => {
    setCurrentYear(newYear);
    loadSummary(newYear);
  };

  const handleOpenPayment = (id, number) => {
    setSelectedProtectId(id);
    setSelectedProtectNumber(number);
    setPaymentModalOpen(true);
  };

  const handleOpenPrint = async id => {
    try {
      const res = await FinanceBudgetService.getProtectById(id);
      if (res.data.success) {
        setPrintData(res.data.data);
        setPrintModalOpen(true);
      }
    } catch (err) {
      alert("ไม่สามารถดึงข้อมูลสำหรับพิมพ์ได้: " + err.message);
    }
  };

  const handleOpenEdit = id => {
    setEditId(id);
    setActiveTab(1); // switch to form tab
  };

  const handleSaveSuccess = () => {
    setEditId(null);
    setActiveTab(0); // return to list tab
    loadSummary(currentYear);
  };

  const handleCancelForm = () => {
    setEditId(null);
    setActiveTab(0);
  };

  return (
    <div className={classes.root}>
      {/* Top Header */}
      <div className={classes.titleHeader}>
        <div>
          <Typography className={classes.mainTitle}>
            <AccountBalanceIcon style={{ marginRight: 10, fontSize: 32 }} />
            ระบบกันเงิน (Budget Protection Management)
          </Typography>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4, flexWrap: "wrap" }}>
            <Typography variant="body2" color="textSecondary">
              โรงพยาบาลโรคผิวหนังเขตร้อนภาคใต้ จังหวัดตรัง
            </Typography>
            <Chip
              size="small"
              label={`คำนวณเฉพาะปีงบประมาณปัจจุบัน: พ.ศ. ${currentYear}`}
              style={{
                backgroundColor: "#dbeafe",
                color: "#1e40af",
                fontWeight: "bold",
                fontSize: "12px"
              }}
            />
          </div>
        </div>

        {/* Year Selector */}
        <Box display="flex" alignItems="center">
          <Typography
            variant="body2"
            style={{ fontWeight: 600, color: "#334155", marginRight: 8, whiteSpace: "nowrap" }}
          >
            ปีงบประมาณ:
          </Typography>
          <TextField
            select
            size="small"
            variant="outlined"
            value={currentYear}
            onChange={e => handleYearChange(e.target.value)}
            style={{ minWidth: 150, backgroundColor: "#ffffff" }}
            SelectProps={{
              style: { fontSize: "14px", height: 38 }
            }}
          >
            {years.length > 0 ? (
              years.map(y => (
                <MenuItem key={y.value} value={y.value}>
                  {y.label || `ปีงบประมาณ ${y.value}`}
                </MenuItem>
              ))
            ) : (
              <MenuItem value={currentYear}>ปีงบประมาณ {currentYear}</MenuItem>
            )}
          </TextField>
        </Box>
      </div>

      {/* KPI Overview Cards - คำนวณเฉพาะปีงบประมาณปัจจุบัน */}
      <Grid container spacing={2} style={{ marginBottom: 20 }}>
        {/* 1. งบประมาณโครงการรวม */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            className={classes.kpiCard}
            style={{ borderLeftColor: "#2563eb" }}
          >
            <CardContent>
              <div className={classes.kpiHeader}>
                <span className={classes.kpiLabel}>งบประมาณโครงการรวม</span>
                <AccountBalanceIcon style={{ color: "#2563eb", fontSize: 24 }} />
              </div>
              <div className={classes.kpiValue} style={{ color: "#1e3a8a" }}>
                {loadingSummary ? (
                  <CircularProgress size={20} />
                ) : (
                  `${summary.totalProjectBudget.toLocaleString("th-TH", {
                    minimumFractionDigits: 2
                  })} ฿`
                )}
              </div>
              <div className={classes.kpiSubtext}>
                งบจัดสรรตามแผนโครงการ ปี {currentYear}
              </div>
            </CardContent>
          </Card>
        </Grid>

        {/* 2. ยอดเงินที่ขออนุมัติกันไว้ (สะสม) */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            className={classes.kpiCard}
            style={{ borderLeftColor: "#d97706" }}
          >
            <CardContent>
              <div className={classes.kpiHeader}>
                <span className={classes.kpiLabel}>ยอดเงินที่ขออนุมัติกันไว้ (สะสม)</span>
                <ListAltIcon style={{ color: "#d97706", fontSize: 24 }} />
              </div>
              <div className={classes.kpiValue} style={{ color: "#d97706" }}>
                {loadingSummary ? (
                  <CircularProgress size={20} />
                ) : (
                  `${summary.totalProtected.toLocaleString("th-TH", {
                    minimumFractionDigits: 2
                  })} ฿`
                )}
              </div>
              <div className={classes.kpiSubtext}>
                รวม {summary.totalProtectCount.toLocaleString()} รายการใบกันเงิน
              </div>
            </CardContent>
          </Card>
        </Grid>

        {/* 3. ยอดเบิกจ่ายแล้วจริง */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            className={classes.kpiCard}
            style={{ borderLeftColor: "#16a34a" }}
          >
            <CardContent>
              <div className={classes.kpiHeader}>
                <span className={classes.kpiLabel}>ยอดเบิกจ่ายแล้วจริง</span>
                <CheckCircleIcon style={{ color: "#16a34a", fontSize: 24 }} />
              </div>
              <div className={classes.kpiValue} style={{ color: "#16a34a" }}>
                {loadingSummary ? (
                  <CircularProgress size={20} />
                ) : (
                  `${summary.totalPaid.toLocaleString("th-TH", {
                    minimumFractionDigits: 2
                  })} ฿`
                )}
              </div>
              <div className={classes.kpiSubtext}>
                เบิกจ่ายแล้ว {summary.completedProtectCount.toLocaleString()} จาก {summary.totalProtectCount.toLocaleString()} รายการ
              </div>
            </CardContent>
          </Card>
        </Grid>

        {/* 4. ยอดคงเหลือรอเบิกจ่าย */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            className={classes.kpiCard}
            style={{ borderLeftColor: "#dc2626" }}
          >
            <CardContent>
              <div className={classes.kpiHeader}>
                <span className={classes.kpiLabel}>ยอดคงเหลือรอเบิกจ่าย</span>
                <HourglassEmptyIcon style={{ color: "#dc2626", fontSize: 24 }} />
              </div>
              <div className={classes.kpiValue} style={{ color: "#dc2626" }}>
                {loadingSummary ? (
                  <CircularProgress size={20} />
                ) : (
                  `${summary.totalPendingPayment.toLocaleString("th-TH", {
                    minimumFractionDigits: 2
                  })} ฿`
                )}
              </div>
              <div className={classes.kpiSubtext}>
                รอเบิกจ่าย {summary.pendingProtectCount.toLocaleString()} รายการ
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Navigation Tabs */}
      <Paper className={classes.tabNavigation}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => {
            if (v !== 1) setEditId(null);
            setActiveTab(v);
          }}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<ListAltIcon />} label="รายการใบกันเงิน" />
          <Tab
            icon={<AddBoxIcon />}
            label={editId ? "แก้ไขใบกันเงิน" : "บันทึกใบกันเงินใหม่"}
          />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <Box mt={2}>
        {activeTab === 0 && (
          <ProtectMoneyList
            onAddNew={() => {
              setEditId(null);
              setActiveTab(1);
            }}
            onEdit={handleOpenEdit}
            onManagePayment={handleOpenPayment}
            onPrint={handleOpenPrint}
          />
        )}

        {activeTab === 1 && (
          <ProtectMoneyForm
            editId={editId}
            onSaveSuccess={handleSaveSuccess}
            onCancel={handleCancelForm}
          />
        )}
      </Box>

      {/* Payment Manager Dialog */}
      <ProtectPaymentManager
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        protectId={selectedProtectId}
        protectNumber={selectedProtectNumber}
        onUpdated={loadSummary}
      />

      {/* Print Protect Voucher Dialog */}
      <PrintProtectDialog
        open={printModalOpen}
        onClose={() => setPrintModalOpen(false)}
        data={printData}
      />
    </div>
  );
}
